/**
 * Error rendering for this console — the settlement actions (plan §3.7), and the
 * two paged reads that feed them.
 *
 * Everything here branches on the mirrored error CODE, never on message text.
 * The eight `expert_payment.*` codes are locked to the api by a set-equality
 * test in `core/apiErrorCodes.test.ts`, so a code is a contract; a message is
 * not.
 *
 * The load-bearing decision is `not_claimable` on the pay path. The ticket's
 * AC3 names `duplicate_live_payment` as the safe double-click outcome, but that
 * is not the code that fires: the pay-claim CAS returns
 * `expert_payment.not_claimable` when it loses (and emits
 * SettlementDoublePayAttempt). `duplicate_live_payment` comes from CreatePayment
 * and is reachable here only through the manual rail, in the narrow window where
 * a cancel freed the live slot mid-insert. Rendering the AC literally would have
 * painted the ordinary double-click red — the exact alarm it exists to prevent.
 *
 * So on the pay path `not_claimable` is INFORMATIONAL: the guard worked, the
 * expert was paid exactly once, and the honest response is to re-read the row.
 * It is not neutral everywhere — a lost cancel CAS, a lost manual-record CAS and
 * a force-fail over a live transfer all return it too — which is why the
 * severity depends on the action and not on the code alone.
 */
import { ERROR_CODES, firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

/** Which mutation produced the error; `not_claimable` reads differently per rail. */
export type SettlementAction = 'pay' | 'cancel' | 'resolve' | 'record_manual';

export type ErrorTone = 'info' | 'warning' | 'error';

export interface SettlementError {
  code: string | undefined;
  tone: ErrorTone;
  message: string;
  /** True when the row's real state is now unknown to us and must be re-read. */
  refetch: boolean;
  /**
   * True when execution is frozen platform-wide. The caller latches this for the
   * session so every Pay button afterwards explains itself, rather than each one
   * discovering the freeze by failing.
   */
  executionFrozen: boolean;
}

const GENERIC = 'That action could not be completed. Try again, or re-read the row.';

export function describeSettlementError(err: unknown, action: SettlementAction): SettlementError {
  const code = firstErrorCode(err);

  switch (code) {
    case ERROR_CODES.EXPERT_PAYMENT_NOT_CLAIMABLE:
      if (action === 'pay') {
        return {
          code,
          tone: 'info',
          message:
            'Already settled — this payment was claimed by another attempt, so nothing further ' +
            'happened here. The expert is paid exactly once. Refreshing to show its real state.',
          refetch: true,
          executionFrozen: false,
        };
      }
      return {
        code,
        tone: 'warning',
        message:
          action === 'resolve'
            ? 'This payment can no longer be resolved that way — a transfer exists for it, or ' +
              'someone else has already settled it. Refreshing to show its real state.'
            : 'This payment is no longer in a state that can be canceled — someone else acted ' +
              'on it first. Refreshing to show its real state.',
        refetch: true,
        executionFrozen: false,
      };

    case ERROR_CODES.EXPERT_PAYMENT_PAYOUTS_NOT_ENABLED:
      return {
        code,
        tone: 'warning',
        message:
          'Stripe payouts are not enabled for this expert, so no money moved and no transfer ' +
          'was created. The row is marked blocked until they finish their payout setup.',
        refetch: true,
        executionFrozen: false,
      };

    case ERROR_CODES.EXPERT_PAYMENT_EXECUTION_DISABLED:
      return {
        code,
        tone: 'warning',
        message:
          'Payment execution is frozen platform-wide. Nothing was attempted. Canceling, ' +
          'recording a manual payment and resolving all still work.',
        refetch: false,
        executionFrozen: true,
      };

    case ERROR_CODES.EXPERT_PAYMENT_TOO_FRESH:
      return {
        code,
        tone: 'warning',
        message:
          'Not yet — this payment was claimed too recently and its executor may still be ' +
          'mid-transfer. Wait for the cutoff to pass, then resolve it.',
        refetch: false,
        executionFrozen: false,
      };

    case ERROR_CODES.EXPERT_PAYMENT_AMOUNT_IMMUTABLE:
      return {
        code,
        tone: 'error',
        message:
          'A live payment already exists for this source with a different amount, currency or ' +
          'payee. Amounts are frozen at creation — cancel the existing row rather than ' +
          'recording a second one.',
        refetch: false,
        executionFrozen: false,
      };

    case ERROR_CODES.EXPERT_PAYMENT_DUPLICATE_LIVE_PAYMENT:
      return {
        code,
        tone: 'warning',
        message:
          'A live payment for this source and kind was created while this one was being ' +
          'recorded. Nothing was written. Re-read the queue and retry if it is still needed.',
        refetch: true,
        executionFrozen: false,
      };

    case ERROR_CODES.EXPERT_PAYMENT_NOT_FOUND:
      return {
        code,
        tone: 'warning',
        message: 'This payment no longer exists. Refreshing the queue.',
        refetch: true,
        executionFrozen: false,
      };

    case ERROR_CODES.EXPERT_PAYMENT_INVALID_INPUT:
      return {
        code,
        tone: 'error',
        // The api interpolates the offending value into this one, so its own
        // message is more useful than anything static.
        message: apiMessage(err) ?? getDefaultErrorMessage(code) ?? GENERIC,
        refetch: false,
        executionFrozen: false,
      };

    default:
      return {
        code,
        tone: 'error',
        message: (code ? getDefaultErrorMessage(code) : undefined) ?? GENERIC,
        refetch: false,
        executionFrozen: false,
      };
  }
}

/**
 * Why the next page never arrived.
 *
 * Both paged reads on this console — the queue and the ledger — go through the
 * api's `paging` package, so both can come back with `paging.cursor_invalid` (a
 * cursor the server will not resume from) or `paging.invalid_argument` (a
 * `first` outside 1–1000). They are told apart from a generic failure because
 * only one of the three is worth retrying: a rejected cursor is rejected
 * identically every time, so "try again" would be wrong advice.
 */
export function describePagingError(err: unknown): string {
  switch (firstErrorCode(err)) {
    case ERROR_CODES.PAGING_CURSOR_INVALID:
      return 'That page marker is no longer valid. Start the list again from the top.';
    case ERROR_CODES.PAGING_INVALID_ARGUMENT:
      return 'The server refused that page size. Reload the page and try again.';
    default:
      return 'Couldn’t load the next page. Nothing already shown has changed — try again.';
  }
}

/**
 * Why the ledger report itself failed.
 *
 * Split from `describePagingError` because the first read sends no cursor, so
 * blaming one would be a guess. A year outside the window the api bounds
 * `paid_at` to comes back as `expert_payment.invalid_input` with the accepted
 * range interpolated into the message, which is more useful than any static
 * line; everything else is a read failure that the year has nothing to do with,
 * and telling an operator to "check the year" there sends them to fix something
 * that is not broken.
 */
export function describeLedgerRunError(err: unknown): string {
  const code = firstErrorCode(err);
  if (code === ERROR_CODES.EXPERT_PAYMENT_INVALID_INPUT) {
    return apiMessage(err) ?? 'That year is outside the range the ledger covers.';
  }
  if (code === ERROR_CODES.PAGING_CURSOR_INVALID || code === ERROR_CODES.PAGING_INVALID_ARGUMENT) {
    return describePagingError(err);
  }
  return 'Couldn’t run the report. Nothing was changed — try again in a moment.';
}

/**
 * The api's own message, when it carries detail a static string cannot.
 *
 * Only used for `invalid_input`, where the server names the field and the bound
 * it violated. Never used to BRANCH — branching stays on codes.
 */
function apiMessage(err: unknown): string | undefined {
  const message = (err as { message?: unknown })?.message;
  return typeof message === 'string' && message.trim().length > 0 ? message : undefined;
}
